import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Checkbox,
  Alert,
} from '@mui/joy';
import { materialCategoriesApi, type MaterialCategory } from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';
import { MATERIAL_UNIT_TYPES } from './materialConstants';

interface Props {
  open: boolean;
  onClose: () => void;
  category: MaterialCategory | null;
  onSave: () => void;
}

const emptyForm = {
  name: '',
  description: '',
  unit_type: 'area',
  display_order: '0',
  is_active: true,
};

export default function MaterialCategoryForm({ open, onClose, category, onSave }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          name: category.name,
          description: category.description || '',
          unit_type: category.unit_type,
          display_order: String(category.display_order ?? 0),
          is_active: category.is_active,
        });
      } else {
        setFormData(emptyForm);
      }
      setError(null);
    }
  }, [open, category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description,
        unit_type: formData.unit_type as MaterialCategory['unit_type'],
        display_order: parseInt(formData.display_order, 10) || 0,
        is_active: formData.is_active,
      };

      if (category) {
        await materialCategoriesApi.update(category.id, payload);
        showSuccess('Material category updated successfully');
      } else {
        await materialCategoriesApi.create(payload);
        showSuccess('Material category created successfully');
      }
      onSave();
      onClose();
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        'Failed to save material category';
      setError(detail);
      showError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90%', maxWidth: 500 }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 2 }}>
          {category ? 'Edit Material Category' : 'Add Material Category'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && <Alert color="danger">{error}</Alert>}

            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Shingles, Underlayment"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl required sx={{ flex: 1 }}>
                <FormLabel>Unit Type</FormLabel>
                <Select
                  value={formData.unit_type}
                  onChange={(_, value) => setFormData({ ...formData, unit_type: value as string })}
                >
                  {MATERIAL_UNIT_TYPES.map((u) => (
                    <Option key={u.value} value={u.value}>
                      {u.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 140 }}>
                <FormLabel>Display Order</FormLabel>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                  slotProps={{ input: { min: 0, step: 1 } }}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <Checkbox
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading} sx={{ flex: 1 }}>
                {category ? 'Update' : 'Create'}
              </Button>
              <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ flex: 1 }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
